import React, { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { formControls } from '@sw-sw/lib-form';
import { ActionButtons } from '../Shared/ActionButtons';
import AppContext from '../../contexts/AppContext';
import tenantApi from '../../utils/api/tenant';

// lib-form's rich-text control (Quill) — configured with a toolbar, inline-style
// size/align attributors, and token-insert buttons inside @sw-sw/lib-form.
const RichTextEditor = formControls.richText as unknown as React.FC<{
  value: string;
  onChange: (html: string) => void;
  tokens?: { label: string; value: string }[];
  minHeight?: number;
}>;

export default function AuthorizationAgreementSettings() {
  const appStore = useContext(AppContext);
  const user = appStore.get<any>('user') || {};
  const tenantId = user.userTenantRoles?.[0]?.tenant_id;

  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [collapse, setCollapse] = useState(true);
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) return undefined;
    let isMounted = true;

    Promise.all([
      tenantApi.features.index(tenantId, 'authorization_agreement'),
      tenantApi.authorizationAgreement.getSettings(tenantId),
    ])
      .then(([features, settings]) => {
        if (!isMounted) return;
        setFeatureEnabled(!!features?.[0]?.is_enabled);
        setBodyTemplate(settings.agreement_body_template ?? '');
      })
      .catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [tenantId]);

  const onSave = async () => {
    setSaving(true);
    try {
      const updated = await tenantApi.authorizationAgreement.updateSettings(
        tenantId,
        {
          agreement_body_template: bodyTemplate,
        },
      );

      setBodyTemplate(updated.agreement_body_template ?? '');
      toast.success('Agreement settings saved successfully!');
    } catch {
      toast.error('Error saving agreement settings');
    } finally {
      setSaving(false);
    }
  };

  if (!featureEnabled) return null;

  return (
    <div className='settings-tenant-main-container'>
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>Authorization &amp; Agreement</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr />
      <br />
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
      >
        <div style={{ maxWidth: 760 }}>
          <label
            style={{
              display: 'block',
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 4,
            }}
          >
            Agreement Legal Language
          </label>
          <p style={{ fontSize: 12, color: '#666', margin: '0 0 8px' }}>
            This is the legal text shown above Exhibit A in the signing window
            and in the generated PDF. Use the toolbar to format the text. Use
            the
            <strong> Insert </strong> buttons to drop in the client name and
            effective date — those are filled in automatically for each project.
          </p>
          <RichTextEditor
            value={bodyTemplate}
            onChange={setBodyTemplate}
            minHeight={340}
            tokens={[
              { label: 'Client Name', value: '{{CLIENT_NAME}}' },
              { label: 'Effective Date', value: '{{EFFECTIVE_DATE}}' },
            ]}
          />
          <div style={{ marginTop: 12 }}>
            <ActionButtons
              primary={saving ? 'Saving...' : 'Save Agreement Settings'}
              onClick={onSave}
              primaryDisabled={saving}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
